// src/pages/Campaigns/SurveyGenie/SurveyBuilder.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, TextField, IconButton, Grid, Card, CardContent,
  Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel,
  Select, MenuItem, Chip, Alert, CircularProgress, Tabs, Tab, Switch,
  FormControlLabel, Divider, Tooltip, Accordion, AccordionSummary, AccordionDetails,
  List, ListItem, ListItemText, ListItemIcon, ListItemSecondaryAction, Snackbar,
  Drawer, Slider, Rating, Radio, RadioGroup, Checkbox, FormGroup, Stack
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, Save as SaveIcon,
  DragIndicator as DragIcon, ExpandMore as ExpandMoreIcon, Preview as PreviewIcon,
  Settings as SettingsIcon, ArrowBack as BackIcon, ContentCopy as CopyIcon,
  QuestionAnswer as QuestionIcon, CheckCircle as CheckIcon, RadioButtonChecked,
  TextFields as TextIcon, StarRate as StarIcon, LinearScale as ScaleIcon,
  ArrowDropDownCircle as DropdownIcon, FormatListNumbered as RankingIcon,
  GridOn as MatrixIcon, CalendarToday as DateIcon, AttachFile as FileIcon,
  Email as EmailIcon, Phone as PhoneIcon, Numbers as NumberIcon, Tune as SliderIcon,
  ThumbUpAlt as YesNoIcon, ViewModule as SectionIcon, Close as CloseIcon,
  LibraryAdd as BankIcon, Visibility as ViewIcon, PlayArrow as PublishIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import axiosInstance from '../../../../utils/axiosInstance';
import { hasPermission } from '../../../../utils/permissionUtils';
import {
  QuestionType, QuestionTypeLabels, SurveyStatus, SurveyStatusLabels,
  getStatusColor, hasOptions, getDefaultSettings
} from './surveyEnums';

// Question Type Icon Map
const QuestionTypeIcons = {
  SINGLE_CHOICE: <RadioButtonChecked />,
  MULTIPLE_CHOICE: <CheckIcon />,
  TEXT_SHORT: <TextIcon />,
  TEXT_LONG: <TextIcon />,
  RATING_SCALE: <StarIcon />,
  LIKERT_SCALE: <ScaleIcon />,
  DROPDOWN: <DropdownIcon />,
  RANKING: <RankingIcon />,
  MATRIX: <MatrixIcon />,
  DATE: <DateIcon />,
  FILE_UPLOAD: <FileIcon />,
  NET_PROMOTER_SCORE: <StarIcon />,
  EMAIL: <EmailIcon />,
  PHONE: <PhoneIcon />,
  NUMBER: <NumberIcon />,
  SLIDER: <SliderIcon />,
  YES_NO: <YesNoIcon />,
  SECTION_BREAK: <SectionIcon />
};

// --- MOVED OUTSIDE: Question Type Selector ---
const QuestionTypeSelector = ({ open, onClose, onSelect }) => (
  <Drawer
    anchor="right"
    open={open}
    onClose={onClose}
  >
    <Box sx={{ width: 350, p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Add Question
      </Typography>
      <Divider sx={{ mb: 2 }} />
      
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        Basic Questions
      </Typography>
      <List dense>
        {['SINGLE_CHOICE', 'MULTIPLE_CHOICE', 'TEXT_SHORT', 'TEXT_LONG', 'DROPDOWN', 'YES_NO'].map(type => (
          <ListItem 
            key={type} 
            button 
            onClick={() => onSelect(type)}
            sx={{ borderRadius: 1, mb: 0.5 }}
          >
            <ListItemIcon>{QuestionTypeIcons[type]}</ListItemIcon>
            <ListItemText primary={QuestionTypeLabels[type]} />
          </ListItem>
        ))}
      </List>

      <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
        Rating & Scale
      </Typography>
      <List dense>
        {['RATING_SCALE', 'LIKERT_SCALE', 'NET_PROMOTER_SCORE', 'SLIDER'].map(type => (
          <ListItem 
            key={type} 
            button 
            onClick={() => onSelect(type)}
            sx={{ borderRadius: 1, mb: 0.5 }}
          >
            <ListItemIcon>{QuestionTypeIcons[type]}</ListItemIcon>
            <ListItemText primary={QuestionTypeLabels[type]} />
          </ListItem>
        ))}
      </List>

      <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
        Advanced
      </Typography>
      <List dense>
        {['MATRIX', 'RANKING', 'DATE', 'FILE_UPLOAD', 'EMAIL', 'PHONE', 'NUMBER'].map(type => (
          <ListItem 
            key={type} 
            button 
            onClick={() => onSelect(type)}
            sx={{ borderRadius: 1, mb: 0.5 }}
          >
            <ListItemIcon>{QuestionTypeIcons[type]}</ListItemIcon>
            <ListItemText primary={QuestionTypeLabels[type]} />
          </ListItem>
        ))}
      </List>

      <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
        Layout
      </Typography>
      <List dense>
        <ListItem 
          button 
          onClick={() => onSelect('SECTION_BREAK')}
          sx={{ borderRadius: 1 }}
        >
          <ListItemIcon>{QuestionTypeIcons['SECTION_BREAK']}</ListItemIcon>
          <ListItemText primary={QuestionTypeLabels['SECTION_BREAK']} />
        </ListItem>
      </List>
    </Box>
  </Drawer>
);

// --- MOVED OUTSIDE: Question Editor Panel ---
const QuestionEditor = ({ question, onClose, onUpdate }) => {
  const [localQuestion, setLocalQuestion] = useState(question);

  useEffect(() => {
    setLocalQuestion(question);
  }, [question]);

  const handleChange = (field, value) => {
    setLocalQuestion({ ...localQuestion, [field]: value });
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...(localQuestion.options || [])];
    newOptions[index] = { ...newOptions[index], option_text: value };
    setLocalQuestion({ ...localQuestion, options: newOptions });
  };

  const addOption = () => {
    const newOptions = [...(localQuestion.options || [])];
    newOptions.push({
      option_text: `Option ${newOptions.length + 1}`,
      order_index: newOptions.length
    });
    setLocalQuestion({ ...localQuestion, options: newOptions });
  };

  const removeOption = (index) => {
    const newOptions = localQuestion.options.filter((_, i) => i !== index);
    setLocalQuestion({ ...localQuestion, options: newOptions });
  };

  const handleSave = () => {
    onUpdate(localQuestion);
  };

  return (
    <Paper sx={{ p: 3, height: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Typography variant="h6">
          Edit Question
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider sx={{ mb: 3 }} />

      <Stack spacing={3}>
        {/* Question Text */}
        <TextField
          fullWidth
          label="Question Text"
          value={localQuestion.question_text || ''}
          onChange={(e) => handleChange('question_text', e.target.value)}
          multiline
          rows={2}
        />

        {/* Question Type (Read-only) */}
        <Box>
          <Chip
            icon={QuestionTypeIcons[localQuestion.question_type]}
            label={QuestionTypeLabels[localQuestion.question_type]}
            color="primary"
            variant="outlined"
          />
        </Box>

        {/* Help Text */}
        <TextField
          fullWidth
          label="Help Text (Optional)"
          value={localQuestion.help_text || ''}
          onChange={(e) => handleChange('help_text', e.target.value)}
          placeholder="Add instructions or hints for respondents"
        />

        {/* Required Toggle */}
        <FormControlLabel
          control={
            <Switch
              checked={localQuestion.is_required || false}
              onChange={(e) => handleChange('is_required', e.target.checked)}
            />
          }
          label="Required"
        />

        {/* Options for choice-based questions */}
        {hasOptions(localQuestion.question_type) && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Options
            </Typography>
            {localQuestion.options?.map((option, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  value={option.option_text || ''}
                  onChange={(e) => handleOptionChange(index, e.target.value)}
                />
                <IconButton 
                  size="small" 
                  onClick={() => removeOption(index)}
                  disabled={localQuestion.options.length <= 2}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={addOption}
            >
              Add Option
            </Button>
          </Box>
        )}

        {/* Rating Scale Settings */}
        {localQuestion.question_type === 'RATING_SCALE' && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Scale Range
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Min"
                  size="small"
                  value={localQuestion.settings?.min_value || 1}
                  onChange={(e) => handleChange('settings', {
                    ...localQuestion.settings,
                    min_value: parseInt(e.target.value)
                  })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Max"
                  size="small"
                  value={localQuestion.settings?.max_value || 5}
                  onChange={(e) => handleChange('settings', {
                    ...localQuestion.settings,
                    max_value: parseInt(e.target.value)
                  })}
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Likert Scale Settings */}
        {localQuestion.question_type === 'LIKERT_SCALE' && (
          <FormControl fullWidth size="small">
            <InputLabel>Scale Type</InputLabel>
            <Select
              value={localQuestion.settings?.scale_type || '5_point'}
              onChange={(e) => handleChange('settings', {
                ...localQuestion.settings,
                scale_type: e.target.value
              })}
              label="Scale Type"
            >
              <MenuItem value="5_point">5-Point Scale</MenuItem>
              <MenuItem value="7_point">7-Point Scale</MenuItem>
            </Select>
          </FormControl>
        )}

        {/* Slider Settings */}
        {localQuestion.question_type === 'SLIDER' && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Slider Range
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Min"
                  size="small"
                  value={localQuestion.settings?.min_value || 0}
                  onChange={(e) => handleChange('settings', {
                    ...localQuestion.settings,
                    min_value: parseInt(e.target.value)
                  })}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Max"
                  size="small"
                  value={localQuestion.settings?.max_value || 100}
                  onChange={(e) => handleChange('settings', {
                    ...localQuestion.settings,
                    max_value: parseInt(e.target.value)
                  })}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Step"
                  size="small"
                  value={localQuestion.settings?.step || 1}
                  onChange={(e) => handleChange('settings', {
                    ...localQuestion.settings,
                    step: parseInt(e.target.value)
                  })}
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Text Length Settings */}
        {['TEXT_SHORT', 'TEXT_LONG'].includes(localQuestion.question_type) && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Character Limits
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Min Length"
                  size="small"
                  value={localQuestion.settings?.min_length || ''}
                  onChange={(e) => handleChange('settings', {
                    ...localQuestion.settings,
                    min_length: e.target.value ? parseInt(e.target.value) : null
                  })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Max Length"
                  size="small"
                  value={localQuestion.settings?.max_length || ''}
                  onChange={(e) => handleChange('settings', {
                    ...localQuestion.settings,
                    max_length: e.target.value ? parseInt(e.target.value) : null
                  })}
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Save Button */}
        <Box sx={{ pt: 2 }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            fullWidth
          >
            Save Changes
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
};

// --- MOVED OUTSIDE: Question Card ---
const QuestionCard = ({ question, index, provided, selectedQuestionId, onSelect, onDuplicate, onDelete }) => (
  <Card
    ref={provided.innerRef}
    {...provided.draggableProps}
    sx={{
      mb: 2,
      border: selectedQuestionId === question.id ? '2px solid' : '1px solid',
      borderColor: selectedQuestionId === question.id ? 'primary.main' : 'grey.300',
      cursor: 'pointer',
      '&:hover': {
        borderColor: 'primary.light'
      }
    }}
    onClick={() => onSelect(question)}
  >
    <CardContent sx={{ py: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
        <Box {...provided.dragHandleProps} sx={{ mr: 1, mt: 0.5, color: 'grey.500' }}>
          <DragIcon />
        </Box>
        
        <Box sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
              Q{index + 1}
            </Typography>
            <Chip
              icon={QuestionTypeIcons[question.question_type]}
              label={QuestionTypeLabels[question.question_type]}
              size="small"
              variant="outlined"
            />
            {question.is_required && (
              <Chip label="Required" size="small" color="error" sx={{ ml: 1 }} />
            )}
          </Box>
          
          <Typography variant="body1">
            {question.question_text}
          </Typography>
          
          {question.help_text && (
            <Typography variant="caption" color="text.secondary">
              {question.help_text}
            </Typography>
          )}
        </Box>
        
        <Box>
          <Tooltip title="Duplicate">
            <IconButton size="small" onClick={(e) => {
              e.stopPropagation();
              onDuplicate(question);
            }}>
              <CopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={(e) => {
              e.stopPropagation();
              onDelete(question.id);
            }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// --- MOVED OUTSIDE: Survey Settings Drawer ---
const SurveySettingsDrawer = ({ open, onClose, survey, setSurvey, onSave }) => (
  <Drawer
    anchor="right"
    open={open}
    onClose={onClose}
  >
    <Box sx={{ width: 400, p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Survey Settings
      </Typography>
      <Divider sx={{ mb: 3 }} />

      <Stack spacing={3}>
        <TextField
          fullWidth
          label="Survey Title"
          value={survey?.title || ''}
          onChange={(e) => setSurvey({ ...survey, title: e.target.value })}
        />

        <TextField
          fullWidth
          multiline
          rows={3}
          label="Description"
          value={survey?.description || ''}
          onChange={(e) => setSurvey({ ...survey, description: e.target.value })}
        />

        <TextField
          fullWidth
          label="Welcome Message"
          multiline
          rows={2}
          value={survey?.welcome_message || ''}
          onChange={(e) => setSurvey({ ...survey, welcome_message: e.target.value })}
        />

        <TextField
          fullWidth
          label="Thank You Message"
          multiline
          rows={2}
          value={survey?.thank_you_message || ''}
          onChange={(e) => setSurvey({ ...survey, thank_you_message: e.target.value })}
        />

        <Divider />

        <FormControlLabel
          control={
            <Switch
              checked={survey?.is_anonymous || false}
              onChange={(e) => setSurvey({ ...survey, is_anonymous: e.target.checked })}
            />
          }
          label="Anonymous Responses"
        />

        <FormControlLabel
          control={
            <Switch
              checked={survey?.allow_multiple_responses || false}
              onChange={(e) => setSurvey({ ...survey, allow_multiple_responses: e.target.checked })}
            />
          }
          label="Allow Multiple Responses"
        />

        <FormControlLabel
          control={
            <Switch
              checked={survey?.show_progress_bar || false}
              onChange={(e) => setSurvey({ ...survey, show_progress_bar: e.target.checked })}
            />
          }
          label="Show Progress Bar"
        />

        <FormControlLabel
          control={
            <Switch
              checked={survey?.allow_save_continue || false}
              onChange={(e) => setSurvey({ ...survey, allow_save_continue: e.target.checked })}
            />
          }
          label="Save & Continue Later"
        />

        <Divider />

        <TextField
          fullWidth
          type="number"
          label="Response Limit"
          value={survey?.response_limit || ''}
          onChange={(e) => setSurvey({ ...survey, response_limit: e.target.value ? parseInt(e.target.value) : null })}
          helperText="Leave empty for unlimited"
        />

        <TextField
          fullWidth
          type="number"
          label="Time Limit (minutes)"
          value={survey?.time_limit_minutes || ''}
          onChange={(e) => setSurvey({ ...survey, time_limit_minutes: e.target.value ? parseInt(e.target.value) : null })}
          helperText="Leave empty for no time limit"
        />

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              type="date"
              label="Start Date"
              InputLabelProps={{ shrink: true }}
              value={survey?.start_date?.split('T')[0] || ''}
              onChange={(e) => setSurvey({ ...survey, start_date: e.target.value })}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              type="date"
              label="End Date"
              InputLabelProps={{ shrink: true }}
              value={survey?.end_date?.split('T')[0] || ''}
              onChange={(e) => setSurvey({ ...survey, end_date: e.target.value })}
            />
          </Grid>
        </Grid>

        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={onSave}
          fullWidth
        >
          Save Settings
        </Button>
      </Stack>
    </Box>
  </Drawer>
);

// --- MOVED OUTSIDE: Survey Preview ---
const SurveyPreview = ({ open, onClose, survey, questions }) => (
  <Dialog
    fullScreen
    open={open}
    onClose={onClose}
  >
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'grey.300', display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="h6">Preview Mode</Typography>
        <Button onClick={onClose}>Close Preview</Button>
      </Box>
      
      <Box sx={{ flexGrow: 1, overflow: 'auto', p: 4, bgcolor: 'grey.100' }}>
        <Paper sx={{ maxWidth: 700, mx: 'auto', p: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
            {survey?.title}
          </Typography>
          {survey?.description && (
            <Typography variant="body1" color="text.secondary" paragraph>
              {survey.description}
            </Typography>
          )}
          {survey?.welcome_message && (
            <Alert severity="info" sx={{ mb: 3 }}>
              {survey.welcome_message}
            </Alert>
          )}

          <Divider sx={{ my: 3 }} />

          {questions.map((question, index) => (
            <Box key={question.id} sx={{ mb: 4 }}>
              <Typography variant="subtitle1" gutterBottom>
                {index + 1}. {question.question_text}
                {question.is_required && <span style={{ color: 'red' }}> *</span>}
              </Typography>
              {question.help_text && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  {question.help_text}
                </Typography>
              )}
              
              {/* Render question input based on type */}
              {question.question_type === 'SINGLE_CHOICE' && (
                <RadioGroup>
                  {question.options?.map((opt, i) => (
                    <FormControlLabel key={i} value={opt.option_text} control={<Radio />} label={opt.option_text} />
                  ))}
                </RadioGroup>
              )}
              
              {question.question_type === 'MULTIPLE_CHOICE' && (
                <FormGroup>
                  {question.options?.map((opt, i) => (
                    <FormControlLabel key={i} control={<Checkbox />} label={opt.option_text} />
                  ))}
                </FormGroup>
              )}
              
              {question.question_type === 'TEXT_SHORT' && (
                <TextField fullWidth size="small" placeholder="Short answer" />
              )}
              
              {question.question_type === 'TEXT_LONG' && (
                <TextField fullWidth multiline rows={4} placeholder="Long answer" />
              )}
              
              {question.question_type === 'RATING_SCALE' && (
                <Rating 
                  max={question.settings?.max_value || 5} 
                  size="large"
                />
              )}
              
              {question.question_type === 'YES_NO' && (
                <RadioGroup row>
                  <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                  <FormControlLabel value="no" control={<Radio />} label="No" />
                </RadioGroup>
              )}
              
              {question.question_type === 'SLIDER' && (
                <Slider
                  min={question.settings?.min_value || 0}
                  max={question.settings?.max_value || 100}
                  step={question.settings?.step || 1}
                  valueLabelDisplay="auto"
                />
              )}
              
              {question.question_type === 'DROPDOWN' && (
                <FormControl fullWidth size="small">
                  <InputLabel>Select</InputLabel>
                  <Select label="Select">
                    {question.options?.map((opt, i) => (
                      <MenuItem key={i} value={opt.option_text}>{opt.option_text}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              
              {question.question_type === 'DATE' && (
                <TextField type="date" fullWidth size="small" InputLabelProps={{ shrink: true }} />
              )}
              
              {question.question_type === 'EMAIL' && (
                <TextField type="email" fullWidth size="small" placeholder="email@example.com" />
              )}
              
              {question.question_type === 'PHONE' && (
                <TextField type="tel" fullWidth size="small" placeholder="+1 (555) 000-0000" />
              )}
              
              {question.question_type === 'NUMBER' && (
                <TextField type="number" fullWidth size="small" />
              )}
            </Box>
          ))}

          <Divider sx={{ my: 3 }} />

          <Button variant="contained" size="large" fullWidth>
            Submit Survey
          </Button>
        </Paper>
      </Box>
    </Box>
  </Dialog>
);

// --- Main Component ---
const SurveyBuilder = () => {
  const { surveyId, chatbotId } = useParams();
  const navigate = useNavigate();
  
  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [survey, setSurvey] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [settingsDrawer, setSettingsDrawer] = useState(false);
  const [questionTypeDrawer, setQuestionTypeDrawer] = useState(false);
  const [questionBankDialog, setQuestionBankDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  
  // Permissions
  const [canEdit, setCanEdit] = useState(false);
  const [checkingPermissions, setCheckingPermissions] = useState(true);

  // Check permissions
  useEffect(() => {
    const checkPermissions = async () => {
      try {
        const editPerm = await hasPermission('survey_genie', 'edit');
        setCanEdit(editPerm);
      } catch (error) {
        console.error('Error checking permissions:', error);
      } finally {
        setCheckingPermissions(false);
      }
    };
    checkPermissions();
  }, []);

  // Load survey data
  useEffect(() => {
    if (surveyId) {
      loadSurvey();
    }
  }, [surveyId]);

  const loadSurvey = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/api/surveys/details`, {
        params: { survey_id: surveyId }
      });
      
      if (response.data.success) {
        setSurvey(response.data.data);
        setQuestions(response.data.data.questions || []);
      }
    } catch (error) {
      showSnackbar('Error loading survey', 'error');
      console.error('Error loading survey:', error);
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  // Save survey settings
  const handleSaveSurvey = async () => {
    setSaving(true);
    try {
      await axiosInstance.put(`/api/surveys`, survey, {
        params: { survey_id: surveyId }
      });
      showSnackbar('Survey saved successfully', 'success');
      setSettingsDrawer(false); // Close drawer on save
    } catch (error) {
      showSnackbar('Error saving survey', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Add new question
  const handleAddQuestion = async (questionType) => {
    try {
      const newQuestion = {
        question_type: questionType,
        question_text: `New ${QuestionTypeLabels[questionType]} Question`,
        is_required: false,
        page_number: 1,
        order_index: questions.length,
        settings: getDefaultSettings(questionType),
        options: hasOptions(questionType) ? [
          { option_text: 'Option 1', order_index: 0 },
          { option_text: 'Option 2', order_index: 1 }
        ] : []
      };

      const response = await axiosInstance.post(`/api/surveys/questions`, newQuestion, {
        params: { survey_id: surveyId }
      });

      if (response.data.success) {
        setQuestions([...questions, response.data.data]);
        setSelectedQuestion(response.data.data);
        setQuestionTypeDrawer(false);
        showSnackbar('Question added', 'success');
      }
    } catch (error) {
      showSnackbar('Error adding question', 'error');
    }
  };

  // Update question
  const handleUpdateQuestion = async (questionData) => {
    try {
      const response = await axiosInstance.put(`/api/surveys/questions`, questionData, {
        params: { question_id: questionData.id }
      });

      if (response.data.success) {
        setQuestions(questions.map(q => 
          q.id === questionData.id ? response.data.data : q
        ));
        showSnackbar('Question updated', 'success');
      }
    } catch (error) {
      showSnackbar('Error updating question', 'error');
    }
  };

  // Delete question
  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this question?')) return;

    try {
      await axiosInstance.delete(`/api/surveys/questions`, {
        params: { question_id: questionId }
      });

      setQuestions(questions.filter(q => q.id !== questionId));
      if (selectedQuestion?.id === questionId) {
        setSelectedQuestion(null);
      }
      showSnackbar('Question deleted', 'success');
    } catch (error) {
      showSnackbar('Error deleting question', 'error');
    }
  };

  // Duplicate question
  const handleDuplicateQuestion = async (question) => {
    try {
      const duplicatedQuestion = {
        ...question,
        question_text: `${question.question_text} (Copy)`,
        order_index: questions.length
      };
      delete duplicatedQuestion.id;

      const response = await axiosInstance.post(`/api/surveys/questions`, duplicatedQuestion, {
        params: { survey_id: surveyId }
      });

      if (response.data.success) {
        setQuestions([...questions, response.data.data]);
        showSnackbar('Question duplicated', 'success');
      }
    } catch (error) {
      showSnackbar('Error duplicating question', 'error');
    }
  };

  // Handle drag and drop reordering
  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const items = Array.from(questions);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update order indices
    const updatedItems = items.map((item, index) => ({
      ...item,
      order_index: index
    }));

    setQuestions(updatedItems);

    try {
      await axiosInstance.put(`/api/surveys/questions/reorder`, {
        question_orders: updatedItems.map(q => ({ id: q.id, order_index: q.order_index }))
      }, {
        params: { survey_id: surveyId }
      });
    } catch (error) {
      showSnackbar('Error reordering questions', 'error');
      loadSurvey(); // Reload to get original order
    }
  };

  // Publish survey
  const handlePublishSurvey = async () => {
    if (questions.length === 0) {
      showSnackbar('Add at least one question before publishing', 'warning');
      return;
    }

    try {
      await axiosInstance.put(`/api/surveys`, 
        { status: SurveyStatus.ACTIVE },
        { params: { survey_id: surveyId } }
      );
      
      setSurvey({ ...survey, status: SurveyStatus.ACTIVE });
      showSnackbar('Survey published successfully!', 'success');
    } catch (error) {
      showSnackbar('Error publishing survey', 'error');
    }
  };

  if (checkingPermissions || loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5, mt: { xs: 2, sm: 10 } }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!survey) {
    return (
      <Box sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 } }}>
        <Alert severity="error">Survey not found</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/surveys`)} sx={{ mr: 2 }}>
            <BackIcon />
          </IconButton>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              {survey.title}
            </Typography>
            <Chip 
              label={SurveyStatusLabels[survey.status]} 
              size="small"
              color={getStatusColor(survey.status)}
            />
          </Box>
        </Box>

        <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<PreviewIcon />}
            onClick={() => setPreviewMode(true)}
          >
            Preview
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<SettingsIcon />}
            onClick={() => setSettingsDrawer(true)}
          >
            Settings
          </Button>
          {survey.status === SurveyStatus.DRAFT && (
            <Button
              variant="contained"
              size="small"
              startIcon={<PublishIcon />}
              onClick={handlePublishSurvey}
              color="success"
            >
              Publish
            </Button>
          )}
        </Stack>
      </Box>

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Questions List */}
        <Grid item xs={12} md={selectedQuestion ? 7 : 12}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 2 }}>
              <Typography variant="h6">
                Questions ({questions.length})
              </Typography>
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<BankIcon />}
                  onClick={() => setQuestionBankDialog(true)}
                >
                  From Bank
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setQuestionTypeDrawer(true)}
                >
                  Add Question
                </Button>
              </Stack>
            </Box>

            {questions.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <QuestionIcon sx={{ fontSize: 60, color: 'grey.400', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No questions yet
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Start building your survey by adding questions
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setQuestionTypeDrawer(true)}
                >
                  Add Your First Question
                </Button>
              </Box>
            ) : (
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="questions">
                  {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef}>
                      {questions.map((question, index) => (
                        <Draggable
                          key={question.id}
                          draggableId={String(question.id)}
                          index={index}
                        >
                          {(provided) => (
                            <QuestionCard
                              question={question}
                              index={index}
                              provided={provided}
                              selectedQuestionId={selectedQuestion?.id}
                              onSelect={setSelectedQuestion}
                              onDuplicate={handleDuplicateQuestion}
                              onDelete={handleDeleteQuestion}
                            />
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}
          </Paper>
        </Grid>

        {/* Question Editor Panel */}
        {selectedQuestion && (
          <Grid item xs={12} md={5}>
            <QuestionEditor 
              question={selectedQuestion} 
              onClose={() => setSelectedQuestion(null)}
              onUpdate={handleUpdateQuestion}
            />
          </Grid>
        )}
      </Grid>

      {/* Drawers and Dialogs */}
      <QuestionTypeSelector 
        open={questionTypeDrawer} 
        onClose={() => setQuestionTypeDrawer(false)} 
        onSelect={handleAddQuestion}
      />
      
      <SurveySettingsDrawer 
        open={settingsDrawer} 
        onClose={() => setSettingsDrawer(false)}
        survey={survey}
        setSurvey={setSurvey}
        onSave={handleSaveSurvey}
      />
      
      <SurveyPreview 
        open={previewMode}
        onClose={() => setPreviewMode(false)}
        survey={survey}
        questions={questions}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SurveyBuilder;